/**
 * FRONT-14: Onglet Références - Refonte complète
 * 
 * Améliorations vs FRONT-12:
 * - Mode lecture/édition (comme TabPresentation)
 * - Upload sans rechargement complet (mise à jour locale)
 * - Validation fichiers renforcée (images uniquement, pas de PDF)
 * - Pastilles de statut (complète/obligatoire)
 * - Design System Centralis (tailles de police corrigées)
 * - Message "3 références obligatoires"
 */
import React, { useState, useEffect, useCallback } from 'react';
import * as profileService from '../../../services/profileService';
import type { Reference, ReferenceData } from '../../../services/profileService';
import { useProfile } from '../../../contexts/ProfileContext';
import { useAutoSave } from '../../../hooks/useAutoSave';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import './TabReferences.css';

// ============================================
// Constantes de validation
// ============================================
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB


// ============================================
// Composant principal
// ============================================
const TabReferences: React.FC = () => {
  const { refreshCompletion, reportAutoSaveStatus } = useProfile();
  const [initialized, setInitialized] = useState(false);
  const [references, setReferences] = useState<Reference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Charger les références
  const loadReferences = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await profileService.getReferences();
      setReferences(data.references);
    } catch (err) {
      setError('Erreur lors du chargement des références');
      console.error('Erreur chargement références:', err);
      // Initialiser avec 3 références vides en cas d'erreur
      const emptyRefs: Reference[] = [
        { numero: 1, nom_operation: null, client: null, budget: null, annee_livraison: null, descriptif: null, has_photo: false, photo_filename: null, is_complete: false },
        { numero: 2, nom_operation: null, client: null, budget: null, annee_livraison: null, descriptif: null, has_photo: false, photo_filename: null, is_complete: false },
        { numero: 3, nom_operation: null, client: null, budget: null, annee_livraison: null, descriptif: null, has_photo: false, photo_filename: null, is_complete: false },
      ];
      setReferences(emptyRefs);
    } finally {
      setInitialized(true);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  const autoSaveRefs = useAutoSave({
    data: references,
    enabled: initialized,
    saveFn: async (data) => {
      const dataToSend: ReferenceData[] = data.map(ref => ({
        numero: ref.numero,
        nom_operation: ref.nom_operation,
        client: ref.client,
        budget: ref.budget,
        annee_livraison: ref.annee_livraison,
        descriptif: ref.descriptif,
      }));
      await profileService.updateReferences(dataToSend);
      await refreshCompletion();
    },
    delay: 1500,
  });

  // Signaler les modifications en attente au contexte
  useEffect(() => {
    const isPending = autoSaveRefs.status === 'pending' || autoSaveRefs.status === 'saving';
    reportAutoSaveStatus('references', isPending);
    return () => reportAutoSaveStatus('references', false);
  }, [autoSaveRefs.status, reportAutoSaveStatus]);

  // Mise à jour d'un champ (mode édition)
  const handleFieldChange = (numero: number, field: keyof ReferenceData, value: string | number | null) => {
    setReferences(prev => prev.map(ref =>
      ref.numero === numero ? { ...ref, [field]: value } : ref
    ));
  };

  // FRONT-14: Upload photo SANS rechargement complet
  const handlePhotoUpload = async (numero: number, file: File) => {
    setError(null);
    try {
      const result = await profileService.uploadReferencePhoto(numero, file);

      // Mise à jour LOCALE uniquement (pas de reload)
      setReferences(prev => prev.map(ref =>
        ref.numero === numero
          ? { ...ref, has_photo: true, photo_filename: result.data?.filename || file.name }
          : ref
      ));

      setSuccessMessage('Photo uploadée avec succès');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Erreur lors de l\'upload de la photo');
      console.error('Erreur upload photo:', err);
    }
  };

  // Supprimer photo
  const handlePhotoDelete = async (numero: number) => {
    setError(null);
    try {
      await profileService.deleteReferencePhoto(numero);

      // Mise à jour locale
      setReferences(prev => prev.map(ref =>
        ref.numero === numero
          ? { ...ref, has_photo: false, photo_filename: null }
          : ref
      ));

      setSuccessMessage('Photo supprimée');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Erreur lors de la suppression de la photo');
      console.error('Erreur suppression photo:', err);
    }
  };

  // Vérifier si une référence est complète localement (TOUS les champs obligatoires, y compris photo)
  const isReferenceComplete = (ref: Reference): boolean => {
    return !!(ref.nom_operation && ref.client && ref.budget && ref.annee_livraison && ref.descriptif && ref.has_photo);
  };

  if (isLoading) {
    return (
      <div className="tab-references loading">
        <div className="loading-spinner"></div>
        <p>Chargement des références...</p>
      </div>
    );
  }

  // Formulaire toujours visible
  return (
    <div className="tab-references">

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
          <button className="alert-close" onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      <div className="references-list">
        {references.map((ref) => (
          <ReferenceEditCard
            key={ref.numero}
            reference={ref}
            isComplete={isReferenceComplete(ref)}
            // Les 3 references sont obligatoires pour atteindre 100 % : sur une
            // carte incomplete, on designe les champs vides plutot que de laisser
            // le badge "Obligatoire" seul, qui ne dit pas lequel manque.
            showErrors={!isReferenceComplete(ref)}
            onFieldChange={handleFieldChange}
            onPhotoUpload={handlePhotoUpload}
            onPhotoDelete={handlePhotoDelete}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// Composant carte référence - MODE ÉDITION
// ============================================
interface ReferenceEditCardProps {
  reference: Reference;
  isComplete: boolean;
  showErrors: boolean;
  onFieldChange: (numero: number, field: keyof ReferenceData, value: string | number | null) => void;
  onPhotoUpload: (numero: number, file: File) => void;
  onPhotoDelete: (numero: number) => void;
}

const ReferenceEditCard: React.FC<ReferenceEditCardProps> = ({
  reference,
  isComplete,
  showErrors,
  onFieldChange,
  onPhotoUpload,
  onPhotoDelete,
}) => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 1;
  const minYear = 1900;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [yearError, setYearError] = useState<string | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const updatePhotoRef = React.useRef<HTMLInputElement>(null);

  // Charger le preview de la photo (authentifié via Bearer)
  useEffect(() => {
    let objectUrl: string | null = null;
    if (reference.has_photo) {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/profile/references/${reference.numero}/photo`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      })
        .then(res => res.blob())
        .then(blob => {
          objectUrl = URL.createObjectURL(blob);
          setPhotoPreviewUrl(objectUrl);
        })
        .catch(() => setPhotoPreviewUrl(null));
    } else {
      setPhotoPreviewUrl(null);
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference.has_photo, reference.numero, reference.photo_filename]);

  // Télécharger la photo (avec authentification) — conservé pour usage interne éventuel
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleDownloadPhoto = async () => { };

  // FRONT-14: Validation renforcée des fichiers
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Vérifier extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: 'Extension non autorisée. Utilisez : jpg, jpeg ou png' };
    }

    // Vérifier MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return { valid: false, error: 'Type de fichier non autorisé. Seules les images sont acceptées.' };
    }

    // Vérifier taille
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'Fichier trop volumineux (max 10 MB)' };
    }

    return { valid: true };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // FRONT-14: Validation renforcée
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error!);
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    await onPhotoUpload(reference.numero, file);
    setIsUploading(false);

    // Reset input
    e.target.value = '';
  };

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onFieldChange(reference.numero, 'budget', value ? parseFloat(value) : null);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const year = value ? parseInt(value, 10) : null;

    // Validation de l'année
    if (year !== null) {
      if (year < minYear) {
        setYearError(`L'année doit être supérieure à ${minYear}`);
      } else if (year > maxYear) {
        setYearError(`L'année ne peut pas dépasser ${maxYear}`);
      } else {
        setYearError(null);
      }
    } else {
      setYearError(null);
    }

    onFieldChange(reference.numero, 'annee_livraison', year);
  };

  return (
    <SectionPliable
      className="reference-card edit-mode"
      title={`Référence ${reference.numero}`}
      headerRight={<ReferenceStatusBadge isComplete={isComplete} />}
    >
        <div className="form-row">
          <div className="form-group form-group-large">
            <label htmlFor={`nom_operation_${reference.numero}`} className="required">Nom de l'opération</label>
            <input
              id={`nom_operation_${reference.numero}`}
              type="text"
              value={reference.nom_operation || ''}
              onChange={(e) => onFieldChange(reference.numero, 'nom_operation', e.target.value || null)}
              placeholder="Ex: Construction pont de Normandie"
              maxLength={255}
              className={showErrors && !reference.nom_operation ? 'input-error' : ''}
              required
            />
            {showErrors && !reference.nom_operation && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>

          <div className="form-group form-group-large">
            <label htmlFor={`client_${reference.numero}`} className="required">Client</label>
            <input
              id={`client_${reference.numero}`}
              type="text"
              value={reference.client || ''}
              onChange={(e) => onFieldChange(reference.numero, 'client', e.target.value || null)}
              placeholder="Ex: Département Seine-Maritime"
              maxLength={255}
              className={showErrors && !reference.client ? 'input-error' : ''}
              required
            />
            {showErrors && !reference.client && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>

          <div className="form-group form-group-small">
            <label htmlFor={`budget_${reference.numero}`} className="required">Budget</label>
            <div className="input-with-suffix">
              <input
                id={`budget_${reference.numero}`}
                type="number"
                value={reference.budget ?? ''}
                onChange={handleBudgetChange}
                placeholder="0"
                min="0"
                step="0.01"
                className={showErrors && !reference.budget ? 'input-error' : ''}
                required
              />
              <span className="suffix">€</span>
            </div>
            {showErrors && !reference.budget && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>

          <div className="form-group form-group-small">
            <label htmlFor={`annee_${reference.numero}`} className="required">Année</label>
            <input
              id={`annee_${reference.numero}`}
              type="number"
              value={reference.annee_livraison ?? ''}
              onChange={handleYearChange}
              placeholder="YYYY"
              min={minYear}
              max={maxYear}
              className={yearError || (showErrors && !reference.annee_livraison) ? 'input-error' : ''}
              required
            />
            {yearError && <span className="field-error">{yearError}</span>}
            {!yearError && showErrors && !reference.annee_livraison && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor={`descriptif_${reference.numero}`} className="required">Descriptif</label>
          <textarea
            id={`descriptif_${reference.numero}`}
            value={reference.descriptif || ''}
            onChange={(e) => onFieldChange(reference.numero, 'descriptif', e.target.value || null)}
            placeholder="Décrivez brièvement le projet, vos responsabilités, les résultats obtenus..."
            rows={4}
            maxLength={5000}
            className={showErrors && !reference.descriptif ? 'input-error' : ''}
            required
          />
          <div className="descriptif-footer">
            {showErrors && !reference.descriptif && (
              <span className="field-error">Champ obligatoire</span>
            )}
            <span className="char-count">{(reference.descriptif || '').length}/5000</span>
          </div>
        </div>

        <div className="form-group">
          <label className="required">Photo</label>

          {uploadError && (
            <div className="upload-error">
              <span>{uploadError}</span>
              <button type="button" onClick={() => setUploadError(null)}>×</button>
            </div>
          )}

          {reference.has_photo ? (
            <div className="photo-preview">
              {photoPreviewUrl && (
                <>
                  <img
                    src={photoPreviewUrl}
                    alt={`Référence ${reference.numero}`}
                    className="photo-preview-img photo-preview-img--clickable"
                    onClick={() => setPhotoModalOpen(true)}
                    title="Cliquer pour agrandir"
                  />
                  {photoModalOpen && (
                    <div className="photo-modal-overlay" onClick={() => setPhotoModalOpen(false)}>
                      <div className="photo-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="photo-modal-close" onClick={() => setPhotoModalOpen(false)}>×</button>
                        <img src={photoPreviewUrl} alt={`Référence ${reference.numero}`} className="photo-modal-img" />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="document-card">
                <div className="document-content">
                  <span className="document-name">{reference.photo_filename}</span>
                </div>
              </div>
              <div className="photo-actions">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  ref={updatePhotoRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => updatePhotoRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? 'Upload...' : 'Mettre à jour la photo'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={`photo-dropzone ${isUploading ? 'uploading' : ''} ${showErrors && !reference.has_photo ? 'photo-dropzone--error' : ''}`}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  id={`photo-${reference.numero}`}
                  disabled={isUploading}
                />
                <label htmlFor={`photo-${reference.numero}`}>
                  {isUploading ? (
                    <>
                      <span className="upload-icon">⏳</span>
                      <span>Upload en cours...</span>
                    </>
                  ) : (
                    <>
                      <span className="upload-icon"></span>
                      <span>Cliquez pour sélectionner ou glissez-déposez un fichier</span>
                      <span className="upload-hint">Formats acceptés : .jpg, .jpeg, .png • Max 10MB</span>
                    </>
                  )}
                </label>
              </div>
              {showErrors && !reference.has_photo && (
                <span className="field-error field-error--photo">Une photo est obligatoire</span>
              )}
            </>
          )}
        </div>
    </SectionPliable>
  );
};

// ============================================
// Composant pastille de statut
// ============================================
interface ReferenceStatusBadgeProps {
  isComplete: boolean;
}

const ReferenceStatusBadge: React.FC<ReferenceStatusBadgeProps> = ({ isComplete }) => {
  return (
    <span className={`status-badge ${isComplete ? 'complete' : 'incomplete'}`}>
      {isComplete ? 'Complète' : 'Obligatoire'}
    </span>
  );
};

export default TabReferences;
